import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from '@tanstack/react-query';
import { meApi, spaceInboxApi } from '../services/api';
import { usePlatformEvents } from '../contexts/PlatformEventContext';
import type { PlatformEvent } from '../types/events';
import type {
  InboxCountsResponse,
  InboxItem,
  SpaceInboxCounts,
} from '../types/inbox';
import type { PaginatedResponse } from '../types';

const QUERY_KEY = ['me', 'inboxCounts'] as const;
const STALE_MS = 60_000;
// Mirror useOverview: brief delay after reconnect so server-side state
// has settled before we re-seed counts from the backend.
const RECONNECT_REFETCH_DELAY_MS = 2_000;

type InboxListData = InfiniteData<PaginatedResponse<InboxItem>>;

function clampMin(n: number): number {
  return Math.max(0, n);
}

/**
 * Look up a previously-cached inbox item by id across every loaded inbox
 * list cache (cross-space `['inbox','list']` and per-space `['spaceInbox','list']`).
 * Used to read `labelIds`/`spaceId` for events that only carry an id (resolve,
 * cancel, expire) and to diff old-vs-new labels for relabel events.
 *
 * Caveat: list-cache subscribers (`useInbox`, `useSpaceInbox`) also mutate
 * on these events. If their handler runs before ours, the item is already
 * gone — caller falls back to refetching the counts endpoint.
 */
function findCachedItem(
  queryClient: QueryClient,
  inboxItemId: string,
): InboxItem | undefined {
  const caches = [
    ...queryClient.getQueriesData<InboxListData>({ queryKey: ['inbox', 'list'] }),
    ...queryClient.getQueriesData<InboxListData>({ queryKey: ['spaceInbox', 'list'] }),
  ];
  for (const [, data] of caches) {
    if (!data) continue;
    for (const page of data.pages) {
      const hit = page.items.find((x) => x.inboxItemId === inboxItemId);
      if (hit) return hit;
    }
  }
  return undefined;
}

/**
 * Pending-only counts for every space the caller belongs to. Powers the
 * Gmail-style badges in `InboxLabelRail`. Seeded from `/me/inbox/counts`
 * and kept live by patching deltas onto the `inbox.*` WebSocket stream;
 * re-seeds after a WS reconnect (events fired during the gap are lost).
 *
 * Personal (non-space) items are NOT counted — the backend omits them
 * from the response.
 */
export function useInboxCounts() {
  const queryClient = useQueryClient();
  const { subscribe, isConnected, diagnostics } = usePlatformEvents();
  const lastSyncRef = useRef<number>(0);

  const query = useQuery<InboxCountsResponse>({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const result = await meApi.inboxCounts();
      lastSyncRef.current = Date.now();
      return result;
    },
    staleTime: STALE_MS,
  });

  const refetch = query.refetch;

  const patchCache = useCallback(
    (updater: (prev: InboxCountsResponse) => InboxCountsResponse) => {
      queryClient.setQueryData<InboxCountsResponse>(QUERY_KEY, (prev) =>
        updater(prev ?? { spaces: {} }),
      );
    },
    [queryClient],
  );

  const applyDelta = useCallback(
    (
      spaceId: string,
      labelIds: string[] | undefined,
      delta: 1 | -1,
      bumpAll: boolean,
    ) => {
      patchCache((prev) => {
        const space = prev.spaces[spaceId] ?? { all: 0, byLabel: {} };
        const byLabel = { ...space.byLabel };
        for (const id of labelIds ?? []) {
          byLabel[id] = clampMin((byLabel[id] ?? 0) + delta);
        }
        const all = bumpAll ? clampMin(space.all + delta) : space.all;
        return {
          ...prev,
          spaces: { ...prev.spaces, [spaceId]: { all, byLabel } },
        };
      });
    },
    [patchCache],
  );

  const handleEvent = useCallback(
    (event: PlatformEvent) => {
      const type = event.eventType;
      const d = event.data as Record<string, unknown>;

      if (type === 'inbox.item_added' || type === 'inbox.item.added') {
        const item = d.item as InboxItem | undefined;
        // Personal items (no spaceId) aren't counted server-side either.
        if (!item || !item.spaceId) return;
        applyDelta(item.spaceId, item.labelIds, 1, true);
        return;
      }

      if (type === 'inbox.item_updated' || type === 'inbox.item.updated') {
        const item = d.item as InboxItem | undefined;
        if (!item || !item.spaceId) return;
        const prev = findCachedItem(queryClient, item.inboxItemId);
        if (!prev) {
          // No prior labelIds to diff against — fall back to a refetch.
          void refetch();
          return;
        }
        const oldIds = new Set(prev.labelIds ?? []);
        const newIds = new Set(item.labelIds ?? []);
        const removed = [...oldIds].filter((x) => !newIds.has(x));
        const added = [...newIds].filter((x) => !oldIds.has(x));
        if (removed.length === 0 && added.length === 0) return;
        applyDelta(item.spaceId, removed, -1, false);
        applyDelta(item.spaceId, added, 1, false);
        return;
      }

      if (
        type === 'inbox.item_resolved' ||
        type === 'inbox.item_cancelled' ||
        type === 'inbox.item_expired' ||
        type === 'inbox.item.resolved' ||
        type === 'inbox.item.cancelled' ||
        type === 'inbox.item.expired'
      ) {
        const id = (d.inboxItemId as string) ?? '';
        if (!id) return;
        const prev = findCachedItem(queryClient, id);
        // The event payload only carries `inboxItemId`, so we need the
        // cached item to know which space/labels to decrement. If the
        // list-cache subscriber already removed the row (subscriber-order
        // race), fall back to a refetch.
        if (!prev) {
          void refetch();
          return;
        }
        if (!prev.spaceId) return; // personal — not counted
        applyDelta(prev.spaceId, prev.labelIds, -1, true);
        return;
      }
    },
    [applyDelta, queryClient, refetch],
  );

  useEffect(() => {
    const unsub = subscribe([{ eventType: 'inbox.*' }], handleEvent);
    return unsub;
  }, [subscribe, handleEvent]);

  // Re-seed after a WS reconnect — events fired during the disconnect
  // window are lost, so deltas may have drifted. Skip the initial
  // false→true on first connect (queryFn already runs on mount).
  const prevConnectedRef = useRef(isConnected);
  useEffect(() => {
    const wasConnected = prevConnectedRef.current;
    prevConnectedRef.current = isConnected;
    if (!wasConnected && isConnected && lastSyncRef.current > 0) {
      const t = setTimeout(() => refetch(), RECONNECT_REFETCH_DELAY_MS);
      return () => clearTimeout(t);
    }
  }, [isConnected, refetch]);

  const counts = query.data;

  const getCount = useCallback(
    (spaceId: string, labelId: string): number =>
      counts?.spaces[spaceId]?.byLabel[labelId] ?? 0,
    [counts],
  );

  const getSpaceAll = useCallback(
    (spaceId: string): number => counts?.spaces[spaceId]?.all ?? 0,
    [counts],
  );

  const totalAll = useMemo(
    () =>
      Object.values(counts?.spaces ?? {}).reduce(
        (sum, s) => sum + (s?.all ?? 0),
        0,
      ),
    [counts],
  );

  /** Force a fresh re-derivation server-side and overwrite the cache.
   *  Use for support/debug — `refetch()` is the cheaper everyday refresh. */
  const recount = useCallback(
    async (spaceId: string): Promise<SpaceInboxCounts> => {
      const fresh = await spaceInboxApi.recount(spaceId);
      patchCache((prev) => ({
        ...prev,
        spaces: { ...prev.spaces, [spaceId]: fresh },
      }));
      return fresh;
    },
    [patchCache],
  );

  return {
    counts,
    getCount,
    getSpaceAll,
    totalAll,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch,
    recount,
    isConnected,
    diagnostics,
  };
}
